/**
 * User-administration + user-preferences data access (AGENTS.md §2: UI
 * components must not call supabase.from() directly). Serves UsersView's
 * role/data/delete paths, UserProfilePreferencesModal's system_config/users/
 * tenants writes, OnboardingWizard's tenant upsert, and RoomRackSettingsModal
 * + reporting's settings upserts — with the exact same tables and predicates
 * as the former inline calls.
 */
import { supabase } from '../supabase';

/** Update one user's role. */
export function updateUserRole(tenantId: string | null | undefined, userId: string, role: string) {
  return supabase.from('users').update({ role }).eq('tenant_id', tenantId).eq('id', userId);
}

/** Update one user's data JSONB (absenceManagers etc. — merged by the caller). */
export function updateUserJsonb(tenantId: string | null | undefined, userId: string, data: Record<string, unknown>) {
  return supabase.from('users').update({ data }).eq('tenant_id', tenantId).eq('id', userId);
}

/** Delete one user. */
export function deleteUserAccount(tenantId: string | null | undefined, userId: string) {
  return supabase.from('users').delete().eq('tenant_id', tenantId).eq('id', userId);
}

/** Read one tenant's data JSONB (preferences merge). */
export function fetchTenantData(tenantId: string | null | undefined) {
  return supabase.from('tenants').select('data').eq('id', tenantId).maybeSingle();
}
/** Update one user's preferred_properties + menu_groups. */
export function updateUserPreferences(tenantId: string | null | undefined, userId: string, patch: Record<string, unknown>) {
  return supabase.from('users').update(patch).eq('tenant_id', tenantId).eq('id', userId);
}

/** Upsert the system_config reload flag (reload broadcast). */
export function upsertSystemReload(tenantId: string | null | undefined, reloadFlag: unknown) {
  return supabase.from('system_config').upsert({
    id: 'reload',
    tenant_id: tenantId,
    reload_flag: reloadFlag,
  });
}

/** Update the tenants.data JSONB (preferences merge handled by the caller). */
export function updateTenantData(tenantId: string | null | undefined, data: Record<string, unknown>) {
  return supabase.from('tenants').update({ data }).eq('id', tenantId);
}

/** Upsert the onboarding-completed tenant row. */
export function upsertTenantOnboarding(tenantId: string, data: Record<string, unknown>) {
  return supabase.from('tenants').upsert({ id: tenantId, data });
}

/** Upsert the room-rack color settings row. */
export function upsertRoomRackSettings(tenantId: string, colors: Record<string, unknown>) {
  return supabase.from('settings').upsert({
    id: 'roomRack',
    tenant_id: tenantId,
    colors,
  });
}

/** Upsert the reporting KPI cards settings row (cards is a top-level column). */
export function upsertBookingStatsKpis(tenantId: string, cards: unknown) {
  return supabase.from('settings').upsert({
    id: 'bookingStatsKPIs',
    tenant_id: tenantId,
    cards,
  });
}